// @flow

import type { AssetOperationsById } from 'fc-operator-asset/types'
import type { Asset } from 'fc-model-assets/types'
// Libs
import React, { Component } from 'react'
import _find from 'lodash/find'
import _first from 'lodash/first'
import _values from 'lodash/values'
import _size from 'lodash/size'

// Components
import DataTable from './DataTable'
import { AssetMigrate } from 'hfty-asset-migrate-dom'
import Add from 'fc-icons/src/Add'
import Pencil from 'fc-icons/src/Pencil'
import Delete from 'fc-icons/src/Delete'
import Upload from 'fc-icons/src/Upload'
import EditAssets from './EditAssets'
import Radio from '@react-mdc/radio'
import Select from 'react-select'

import { UploadFieldBorders } from 'fc-upload-borders'

import { FARM, FIELD, PROP_CATEGORY, PROP_ASSET_ID } from 'fc-model-assets'

// Container
import assetsManagerContainer from '../containers/AssetsManager'

import messages from '../messages'
import { injectIntl, intlShape } from 'react-intl'

import Mask from 'fc-masks'

// Styles
import { white, primary, dark, lightGrey } from 'hfty-styles/src/colors'

const styles = {
  container: {
    flex: '1 1 auto',
    display: 'flex',
  },
  panel: {
    width: 300,
    backgroundColor: '#333',
    color: '#fff',
    boxShadow: '-2 0 2px rgba(0, 0, 0, 0.2)',
  },
  editButton: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    padding: '2px 7px',
    marginLeft: '2px',
    marginRight: '2px',
  },
  buttonLabels: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: '2px',
    marginRight: '2px',
  },
  select: {
    width: 200,
    marginRight: 5,
  },
  toolBarGroup: {
    display: 'flex',
    borderRadius: 2,
    alignItems: 'center',
    padding: '3px 7px',
    backgroundColor: white(),
    boxShadow: '0 0 2px rgba(0, 0, 0, 0.2)',
    marginLeft: 10,
  },
  migrationButton: {
    width: 240,
    height: 34,
    padding: '0 6px',
    fontSize: 13,
    border: 'none',
    borderRadius: 0,
    backgroundColor: primary(),
    color: white(),
    cursor: 'pointer',
    outline: 'none',
  },
}

// Types
type Props = {
  selectedIds: Array<number>,
  operations: AssetOperationsById,
  category: ?string,
  actions: {
    onAdd: Function,
    onEdit: Function,
    onDelete: Function,
    onMigrate: Function,
    onUpload: Function,
    onChangeSeason: Function,
    onChangeOperation: Function,
    onCancelOperations: Function,
    onSubmitOperations: Function,
    updateFieldBorderOperation: Function,
    onSubmitUploadOperations: Function,
    onCancelUploadOperations: Function,
    push: Function,
  },
  onMapToAsset: Function,
  viewport: {
    width: number,
  },
  farmSelectorOptions: Array<{ value: number, label: string }>,
  seasonSelectorOptions: Array<{ value: number, label: string }>,
  selectedSeasonId: number,
  selectedFarmId: number,
  seasonSpecificFields: Object,
  uploadOperations: Object,
  growerId: number,
  intl: intlShape,
  assets: Array<Asset>,
  jobs: Object,
}

// Exports
export class AssetsManager extends Component {
  props: Props
  filterAssetsByCategory: Function

  constructor(props: Props) {
    super(props)

    this.filterAssetsByCategory = this.filterAssetsByCategory.bind(this)
    this.filterAssetsBySeason = this.filterAssetsBySeason.bind(this)
    this.uploadBorders = this.uploadBorders.bind(this)
  }

  canEdit(): boolean {
    return this.props.selectedIds.length > 0
  }

  canUpload(): boolean {
    const { assets } = this.props
    if (this.props.selectedIds.length === 1) {
      const asset = _find(assets, (asset: Asset): boolean => asset[PROP_ASSET_ID] === this.props.selectedIds[0]) || {}
      if (asset[PROP_CATEGORY] === FIELD) {
        return !_find(this.props.jobs, { field: asset[PROP_ASSET_ID] })
      }
    }
    return false
  }

  uploadBorders() {
    if (this.canUpload()) {
      const { selectedIds } = this.props
      this.props.actions.onUpload({ selectedId: _first(_values(selectedIds)) })
    }
  }

  filterAssetsByCategory(e: Object) {
    const pathURL = {
      All: '/assets',
      Farm: '/assets/farms',
      Field: '/assets/fields',
    }

    const { push } = this.props.actions

    push(pathURL[e.target.value])
  }

  filterAssetsBySeason(e: Object) {
    this.props.actions.onChangeSeason((e && e.value) || null)
  }

  render(): React.Element<any> {
    return (
      <div data-test="fc-assets-dom/AssetsManager" style={styles.container}>
        <DataTable
          mobile={this.props.viewport.width < 768}
          onClickMap={this.props.onMapToAsset}
          seasonSpecificFields={this.props.seasonSpecificFields}
        >
          <div style={styles.toolBarGroup}>
            <button style={styles.migrationButton} onClick={this.props.actions.onMigrate}>
              {this.props.intl.formatMessage(messages.migrate)}
            </button>
          </div>
          <div style={styles.toolBarGroup}>
            <Select
              id="season.select"
              name="season-selector"
              placeholder={'Seasons'}
              value={this.props.selectedSeasonId}
              options={this.props.seasonSelectorOptions}
              onChange={this.filterAssetsBySeason}
              style={styles.select}
            />
            <label style={styles.buttonLabels} htmlFor="season.select">
              Season
            </label>
          </div>

          <div style={styles.toolBarGroup}>
            <Radio.Default
              id="display.All"
              name="display"
              onChange={this.filterAssetsByCategory}
              value="All"
              defaultChecked
              checked={!this.props.category}
            />
            <label style={styles.buttonLabels} htmlFor="display.All">
              All
            </label>
            <Radio.Default
              id="display.farm"
              name="display"
              onChange={this.filterAssetsByCategory}
              value={FARM}
              checked={this.props.category === FARM}
            />
            <label style={styles.buttonLabels} htmlFor="display.farm">
              Farms
            </label>
            <Radio.Default
              id="display.field"
              name="display"
              onChange={this.filterAssetsByCategory}
              value={FIELD}
              checked={this.props.category === FIELD}
            />
            <label style={styles.buttonLabels} htmlFor="display.field">
              Fields
            </label>
          </div>

          <div style={styles.toolBarGroup}>
            <div
              style={styles.editButton}
              onClick={this.props.actions.onAdd}
              title={this.props.intl.formatMessage(messages.add)}
            >
              <Add color={dark()} />
            </div>
            <div
              style={styles.editButton}
              onClick={this.uploadBorders}
              title={this.props.intl.formatMessage(messages.upload)}
            >
              <Upload color={this.canUpload() ? dark() : lightGrey()} />
            </div>
            <div
              style={styles.editButton}
              onClick={this.props.actions.onEdit}
              title={this.props.intl.formatMessage(messages.edit)}
            >
              <Pencil color={this.canEdit() ? dark() : lightGrey()} />
            </div>
            <div
              style={styles.editButton}
              onClick={this.props.actions.onDelete}
              title={this.props.intl.formatMessage(messages.delete)}
            >
              <Delete color={this.canEdit() ? dark() : lightGrey()} />
            </div>
          </div>
          <Mask enabled={_size(this.props.uploadOperations)} />
        </DataTable>
        <EditAssets
          operations={this.props.operations}
          onChange={this.props.actions.onChangeOperation}
          onSubmit={this.props.actions.onSubmitOperations}
          onCancel={this.props.actions.onCancelOperations}
          farmSelectorOptions={this.props.farmSelectorOptions}
          seasonSelectorOptions={this.props.seasonSelectorOptions}
          growerId={this.props.growerId}
          selectedFarmId={this.props.selectedFarmId}
        />
        <UploadFieldBorders
          uploadOperations={this.props.uploadOperations}
          onChange={this.props.actions.updateFieldBorderOperation}
          onSubmit={this.props.actions.onSubmitUploadOperations}
          onCancel={this.props.actions.onCancelUploadOperations}
          assets={this.props.assets}
        />
        <AssetMigrate />
      </div>
    )
  }
}

export default injectIntl(assetsManagerContainer(AssetsManager))

/*
// notes
*/
